import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js/max';

// BL-177 Step 2d (section 3): THE phone helper. Every screen, email, notification and export formats a number
// through here; nothing else formats phones. Store E.164 only; format for people on the way out.
//
//   +1 (404) 594-2667     +49 163 5959331     +44 20 7946 0958     +52 55 1234 5678
//   Short codes (3-8 digits, no "+") show as-is and "can't reply".
//   Sender names (letters) and anything unparseable come back exactly as typed.

const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;

function clean(value) {
    if (value && typeof value === 'object') value = value.number || value.phone || value.value || value.phone_number || '';
    return typeof value === 'string' ? value.replace(/^whatsapp:/i, '').trim() : '';
}

/** A 3-8 digit sender with no "+", e.g. 72345. These can't be replied to. */
export function isShortCode(value) {
    return /^\d{3,8}$/.test(clean(value));
}

/** True for a name-style sender like AMAZON (letters, no real number). */
export function isAlphaSender(value) {
    const text = clean(value);
    return /[A-Za-z]/.test(text) && !/^\+?[\d\s().-]+$/.test(text);
}

/** Short codes and sender names can't receive a reply. */
export function canReplyTo(value) {
    return !isShortCode(value) && !isAlphaSender(value);
}

function parse(value, defaultCountry) {
    const text = clean(value);
    if (!text) return null;
    return parsePhoneNumberFromString(text, text.startsWith('+') ? undefined : (defaultCountry || undefined)) || null;
}

function layout(parsed) {
    // North America reads (404) 594-2667 after the +1; everyone else uses libphonenumber's own international layout.
    if (parsed.countryCallingCode === '1') return `+1 ${parsed.formatNational()}`;
    return parsed.formatInternational();
}

/**
 * The number the way people in its own country read it. Accepts E.164 or a typed number (pass `defaultCountry`
 * for local-looking input). Returns the input unchanged when it isn't a parseable number.
 */
export function formatPhone(value, defaultCountry) {
    const text = clean(value);
    if (!text) return '';
    if (isShortCode(text)) return text;
    const parsed = parse(text, defaultCountry);
    return parsed && parsed.isPossible() ? layout(parsed) : text;
}

/** Short-code aware label for a thread/contact: "72345 · can't reply", otherwise the formatted number. */
export function formatPhoneLabel(value, defaultCountry) {
    const text = clean(value);
    if (isShortCode(text)) return `${text} · can't reply`;
    return formatPhone(text, defaultCountry);
}

/** E.164 for storage ("+14045942667"), or null when it isn't a valid number. */
export function toE164(value, defaultCountry = 'US') {
    const text = clean(value);
    if (!text || isShortCode(text) || isAlphaSender(text)) return null;
    const parsed = parse(text, defaultCountry);
    return parsed && parsed.isValid() ? parsed.number : null;
}

/** ISO country (US, DE, GB...) of a number, or ''. */
export function phoneCountry(value, defaultCountry) {
    const parsed = parse(value, defaultCountry);
    return (parsed && (parsed.country || '')) || '';
}

/** "Germany" for +49..., or '' when unknown. */
export function phoneCountryName(value, defaultCountry) {
    const country = phoneCountry(value, defaultCountry);
    if (!country) return '';
    try { return (regionNames && regionNames.of(country)) || country; } catch { return country; }
}

/**
 * Format as you type. Returns { text, e164, country, valid }: show `text` in the input, save `e164` (null until
 * the number is complete and valid). A leading "+" means the person is giving the country; otherwise
 * `defaultCountry` (default US) decides.
 */
export function formatAsYouType(input, defaultCountry = 'US') {
    const raw = typeof input === 'string' ? input : '';
    const typed = new AsYouType(raw.trim().startsWith('+') ? undefined : defaultCountry);
    let text = typed.input(raw);
    const country = typed.getCountry() || '';
    const parsed = parsePhoneNumberFromString(raw, raw.trim().startsWith('+') ? undefined : defaultCountry);
    if (parsed && parsed.countryCallingCode === '1' && parsed.isPossible()) text = layout(parsed);
    return { text, e164: parsed && parsed.isValid() ? parsed.number : null, country: country || (parsed && parsed.country) || '', valid: Boolean(parsed && parsed.isValid()) };
}

/** Replace every E.164-looking number inside free text (emails, notification copy) with its formatted form. */
export function formatPhonesInText(text) {
    return String(text ?? '').replace(/\+\d[\d\s]{6,18}\d/g, (match) => {
        const formatted = formatPhone(match.replace(/\s+/g, ''));
        return formatted || match;
    });
}
